// @owners { team: patients }
import { Experimentation } from '@alto/experimentation';
import { getOrderPaymentMethodId } from '~shared/features/checkout/selectors/getOrder';
import { PAYMENT_ERRORS, PAYMENT_METHOD_DATA_TYPES } from '~shared/features/payment-methods/constants';
import { findMatchingPaymentMethod } from '~shared/features/payment-methods/helpers';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { useAchBalanceCheck } from '~shared/queries/useAchBalanceCheck';
import { useOrderPricing } from '~shared/queries/useOrderPricing';
import { useSelectorShared } from '~shared/store';
import { type APIError } from '~shared/types';
import { useGetCheckoutTotalPrice } from './useGetCheckoutTotalPrice';

const isAchPreflightBlockingError = (code: string | undefined): boolean =>
  code === PAYMENT_ERRORS.ACH_INSUFFICIENT_FUNDS || code === PAYMENT_ERRORS.PLAID_ITEM_STALE;

export const useAchPreflightCheck = () => {
  const { value: isEnabled } = Experimentation.useFeatureFlag('ach_preflight_balance_check');
  const paymentMethodId = useSelectorShared(getOrderPaymentMethodId);
  const { orderPricing } = useOrderPricing();
  const estimatedCost = useGetCheckoutTotalPrice({ orderPricing });
  const { paymentMethods } = useGetPaymentMethods();
  const { checkAchBalance } = useAchBalanceCheck();

  const runPreflightCheck = async (): Promise<void> => {
    if (!isEnabled || !paymentMethodId) {
      return;
    }

    const selectedPaymentMethod = findMatchingPaymentMethod(paymentMethods, paymentMethodId);
    if (selectedPaymentMethod?.type !== PAYMENT_METHOD_DATA_TYPES.PLAID_DATA) {
      return;
    }

    // During isOrderPricingLoading, a fast submit tap silently skips preflight (intentional
    // fail-open; BE order check remains authoritative).
    if (estimatedCost === undefined) {
      return;
    }

    try {
      await checkAchBalance(paymentMethodId, estimatedCost);
    } catch (error) {
      const apiError = error as APIError;
      if (isAchPreflightBlockingError(apiError.details?.code)) {
        throw apiError;
      }
    }
  };

  return { runPreflightCheck };
};
